/* eslint-disable @typescript-eslint/no-misused-promises */
import _ from "lodash";
import {Link} from "react-router-dom";
import {z} from "zod";
import {SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {GoogleReCaptchaProvider, useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {Alert, Box, Grid, Paper, Stack, Typography} from "@mui/material";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {
    getResourceErrorMessage,
    isResourceFailed,
    isResourceLoading,
    isResourceSucceeded,
} from "../../utils/resourceUtils";
import {ActionButton} from "../base/ActionButton";
import {UserRegistrationForm, UserRegistrationFormInputs} from "../users/UserRegistrationForm";
import {AppLayout} from "../AppLayout";
import {EmptyHeader} from "../EmptyHeader";
import {RegisterUserBodyType, registerUser} from "../../services/registrationService";
import {DEFAULT_USER_PREFERENCES, validateUserPasswordRequirements} from "../../utils/userUtils";
import {swallowException} from "../../utils/appUtils";
import {loadStringFromStorage} from "../../services/localStorageService";
import {Trans, useTranslation} from "react-i18next";

const CAPTCHA_SITE_KEY = import.meta.env.VITE_APP_CAPTCHA_SITE_KEY as string;

/**
 * The content of Registration page is extracted to RegistrationPageContent component,
 * because useGoogleReCaptcha hook requires a component to be wrapped by
 * GoogleReCaptchaProvider outside of component.
 */
export const RegistrationPage: React.FC = () => (
    <GoogleReCaptchaProvider reCaptchaKey={CAPTCHA_SITE_KEY}>
        <RegistrationPageContent />
    </GoogleReCaptchaProvider>
);

const RegistrationPageContent: React.FC = () => {
    const {t} = useTranslation();
    const {reset, resource, registerUser: register} = useResourceManagement({registerUser});
    const form = useForm({
        defaultValues: REGISTRATION_FORM_INPUTS,
        resolver: zodResolver(REGISTRATION_SCHEMA),
    });
    const {executeRecaptcha} = useGoogleReCaptcha();

    const onSubmit: SubmitHandler<FormInputs> = (data) => {
        reset();

        const registerUserFn = async () => {
            const registrationEnablementCode = loadStringFromStorage(REGISTRATION_ENABLEMENT_CODE_STORAGE_KEY, "");
            const captchaToken = executeRecaptcha ? await executeRecaptcha("register") : "";
            await register(formInputsToRegisterUserBody(data, captchaToken, registrationEnablementCode));
        };

        registerUserFn().catch(swallowException);
    };

    return (
        <AppLayout header={<EmptyHeader />}>
            <Grid
                container
                component="main"
                display="flex"
                justifyContent={{xs: "flex-start", sm: "center"}}
                alignItems={{xs: "flex-start", sm: "center"}}
                height="100%"
            >
                <Grid
                    item
                    component={Paper}
                    elevation={0}
                    square
                    sx={{
                        width: {xs: "100%", sm: "40rem"},
                        borderRadius: "1rem",
                        height: {xs: "100%", sm: "auto"},
                    }}
                >
                    {isResourceFailed(resource) && (
                        <Alert severity="error" sx={{paddingX: "2.5rem"}}>
                            {getResourceErrorMessage(resource)}
                        </Alert>
                    )}
                    {isResourceSucceeded(resource) && (
                        <Alert severity="success" sx={{paddingX: "2.5rem"}}>
                            {t("registration_page.success")}
                        </Alert>
                    )}
                    <Stack
                        direction="column"
                        justifyContent="center"
                        sx={{
                            paddingY: "2rem",
                            paddingX: "3rem",
                        }}
                    >
                        <Stack direction="column" alignItems="center">
                            <Typography component="h1" variant="h5">
                                {t("registration_page.title")}
                            </Typography>
                            <Box sx={{marginTop: "1rem", paddingX: "1.25rem", width: "100%"}}>
                                <form>
                                    <UserRegistrationForm initFormValues={REGISTRATION_FORM_INPUTS} form={form} />
                                    <Typography textAlign="center" fontSize={14} marginTop="1rem">
                                        {t("registration_page.form.policies1")}{" "}
                                        <Trans
                                            i18nKey="registration_page.form.policies2"
                                            components={{
                                                1: (
                                                    <Link
                                                        to="/documentation/IrriWatch_Subscriber_Agreement_v2.pdf"
                                                        target="_blank"
                                                        style={{textDecoration: "none"}}
                                                    />
                                                ),
                                                2: (
                                                    <Link
                                                        to="/documentation/Privacy Statement Hydrosat BV 2023.pdf"
                                                        target="_blank"
                                                        style={{textDecoration: "none"}}
                                                    />
                                                ),
                                            }}
                                        ></Trans>
                                    </Typography>
                                    <ActionButton
                                        type="submit"
                                        fullWidth
                                        color="primary"
                                        icon="none"
                                        sx={{
                                            marginTop: "1.5rem",
                                            marginBottom: "1rem",
                                        }}
                                        onClick={form.handleSubmit(onSubmit)}
                                        disabled={isResourceLoading(resource)}
                                    >
                                        {t("registration_page.form.submit_button")}
                                    </ActionButton>
                                    <Typography textAlign="center">
                                        {t("registration_page.form.signin1")}{" "}
                                        <Link to="/login" style={{textDecoration: "none"}}>
                                            {t("registration_page.form.signin2")}
                                        </Link>
                                    </Typography>
                                </form>
                            </Box>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </AppLayout>
    );
};

const REGISTRATION_SCHEMA = z
    .object({
        email: z.string().email().max(200),
        firstName: z.string().min(1).max(200),
        lastName: z.string().min(1).max(200),
        company: z.string().max(200),
        country: z.any(),
        phone: z.string(),
        password: z.string().min(1).max(200),
        repeatPassword: z.string().min(1).max(200),
        enabledMarketingEmails: z.boolean(),
    })
    .refine((data) => data.email === data.email.toLowerCase(), {
        message: "Email can only contain lowercase characters",
        path: ["email"],
    })
    .refine((data) => data.country !== null, {
        message: "Select a country",
        path: ["country"],
    })
    .refine((data) => data.password === data.repeatPassword, {
        message: "Passwords don't match",
        path: ["repeatPassword"],
    })
    .refine((data) => validateUserPasswordRequirements(data.password), {
        message:
            "Password doesn't meet requirements. Should be minimum 8 characters long, have at least one capital case character, one lowercase character, one number and one special character",
        path: ["password"],
    });

type FormInputs = UserRegistrationFormInputs;

const formInputsToRegisterUserBody = (
    inputs: FormInputs,
    captchaToken: string,
    registrationEnablementCode: string,
): RegisterUserBodyType => ({
    properties: {
        firstName: inputs.firstName,
        lastName: inputs.lastName,
        email: inputs.email,
        company: _.isEmpty(inputs.company) ? null : inputs.company,
        country: inputs.country!,
        phone: _.isEmpty(inputs.phone) ? null : inputs.phone,
    },
    preferences: {
        ...DEFAULT_USER_PREFERENCES,
        enabledMarketingEmails: inputs.enabledMarketingEmails,
    },
    password: inputs.password,
    captcha: captchaToken,
    registrationEnablementCode,
});

const REGISTRATION_FORM_INPUTS: FormInputs = {
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    country: null,
    phone: "",
    password: "",
    repeatPassword: "",
    enabledMarketingEmails: true,
};

const REGISTRATION_ENABLEMENT_CODE_STORAGE_KEY = "registrationEnablementCode";
